import { Download, Share, PlusSquare, MoreVertical, CheckCircle, Smartphone, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePWA, type UsePWAOptions } from '@/hooks/usePWA';
import { getThemeFromDomain } from '@/utils/themeDomain';

type Props = UsePWAOptions & {
  /** Compact card for settings; banner style for login */
  variant?: 'card' | 'banner';
  className?: string;
};

export default function PwaInstallCard({
  variant = 'card',
  className = '',
  appName,
  shortName,
  logoUrl,
}: Props) {
  const { t } = useTranslation();
  const {
    isInstallable,
    isInstalled,
    platform,
    showIosHelp,
    setShowIosHelp,
    installError,
    installPWA,
    brand,
  } = usePWA({ appName, shortName, logoUrl });

  const theme = getThemeFromDomain();
  const label = brand.shortName || (theme === 'kashly' ? 'Kashly' : 'SaleDesk');

  if (isInstalled) {
    if (variant === 'banner') return null;
    return (
      <div className={`rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3 ${className}`}>
        <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-emerald-900">{t('pwa.installedTitle', { name: label })}</p>
          <p className="text-xs text-emerald-700">{t('pwa.installedDesc')}</p>
        </div>
      </div>
    );
  }

  const iosSteps =
    platform === 'ios_safari' || platform === 'ios_other' ? (
      <ol className="mt-3 space-y-2 text-xs text-gray-600 list-decimal list-inside">
        <li className="flex items-start gap-2">
          <Share className="w-3.5 h-3.5 mt-0.5 text-primary-600 shrink-0" />
          <span>
            {platform === 'ios_other' ? t('pwa.iosOpenSafari') : t('pwa.iosTapShare')}
          </span>
        </li>
        <li className="flex items-start gap-2">
          <PlusSquare className="w-3.5 h-3.5 mt-0.5 text-primary-600 shrink-0" />
          <span>{t('pwa.iosAddToHome')}</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-primary-600 shrink-0" />
          <span>{t('pwa.iosTapAdd', { name: label })}</span>
        </li>
      </ol>
    ) : platform.startsWith('android') ? (
      <ol className="mt-3 space-y-2 text-xs text-gray-600 list-decimal list-inside">
        <li className="flex items-start gap-2">
          <MoreVertical className="w-3.5 h-3.5 mt-0.5 text-primary-600 shrink-0" />
          <span>{t('pwa.androidOpenMenu')}</span>
        </li>
        <li className="flex items-start gap-2">
          <Download className="w-3.5 h-3.5 mt-0.5 text-primary-600 shrink-0" />
          <span>{t('pwa.androidInstall')}</span>
        </li>
      </ol>
    ) : (
      <ol className="mt-3 space-y-2 text-xs text-gray-600 list-decimal list-inside">
        <li>{t('pwa.desktopAddressBar')}</li>
        <li>{t('pwa.desktopMenuInstall', { name: label })}</li>
      </ol>
    );

  if (variant === 'banner') {
    return (
      <div className={`w-full ${className}`}>
        <div className="bg-primary-50 border border-primary-200 rounded-2xl p-4 flex items-center justify-between shadow-sm gap-3">
          <div className="min-w-0">
            <p className="text-primary-900 font-bold text-sm">{t('pwa.getApp', { name: label })}</p>
            <p className="text-primary-700 text-xs mt-0.5">{t('pwa.getAppDesc')}</p>
          </div>
          <button
            type="button"
            onClick={() => void installPWA()}
            className="shrink-0 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold py-2 px-4 rounded-xl transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> {t('pwa.install')}
          </button>
        </div>
        {(showIosHelp || installError) && (
          <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3 relative">
            <button
              type="button"
              className="absolute top-2 end-2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowIosHelp(false)}
              aria-label={t('pwa.close')}
            >
              <X className="w-4 h-4" />
            </button>
            {installError && <p className="text-xs text-amber-700 mb-1 pe-6">{installError}</p>}
            <p className="text-xs font-medium text-gray-800">{t('pwa.howToInstall')}</p>
            {iosSteps}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`pt-6 border-t border-gray-100 ${className}`}>
      <div className="flex items-start gap-2 mb-3">
        <Smartphone className="w-5 h-5 text-primary-600 mt-0.5" />
        <div>
          <h3 className="text-base font-semibold text-gray-900">{t('pwa.installApp')}</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('pwa.installDesc', { name: label })}
            {logoUrl ? t('pwa.installDescLogo') : ''}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => void installPWA()}
          className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 hover:border-primary-300 hover:bg-primary-50/40 transition-colors text-start"
        >
          <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-700 overflow-hidden">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="w-full h-full object-contain bg-white" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">
              {isInstallable
                ? t('pwa.installPwa')
                : platform.startsWith('ios')
                  ? t('pwa.addToHomeScreen')
                  : t('pwa.installOrAdd')}
            </p>
            <p className="text-xs text-gray-500">
              {isInstallable
                ? t('pwa.clickToInstall')
                : platform.startsWith('ios')
                  ? t('pwa.safariShareHint')
                  : t('pwa.tapForSteps')}
            </p>
          </div>
        </button>
      </div>

      {(showIosHelp || installError || platform.startsWith('ios')) && (
        <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
          {installError && <p className="text-xs text-amber-700 mb-2">{installError}</p>}
          <p className="text-xs font-medium text-gray-800 mb-1">{t('pwa.installOnDevices')}</p>
          {iosSteps}
        </div>
      )}
      {!showIosHelp && !platform.startsWith('ios') && !isInstallable && (
        <button
          type="button"
          className="mt-2 text-xs text-primary-600 hover:underline"
          onClick={() => setShowIosHelp(true)}
        >
          {t('pwa.showSteps')}
        </button>
      )}
    </div>
  );
}
